/* eslint-disable @typescript-eslint/no-explicit-any */
import { datadogLogs } from '@datadog/browser-logs';
import { HTMLAttributes, useCallback, useEffect, useMemo, useRef } from 'react';
import { Socket } from 'socket.io-client';

import { ParticipantTile } from 'components/ParticipantTile';
import { NODE_ENV } from 'configs';
import { PRESET_TYPE } from 'configs/meeting';
import { useOverlayPanels, useStudentTalkTime } from 'hooks';
import useMeeting from 'hooks/useMeeting';
import useStudentAway from 'hooks/useStudentAway';
import { useMeetingStore } from 'lib/meeting-store';
import {
  extractUserIdFromParticipantId,
  isTutorParticipant,
  shouldShowForParent,
} from 'utils/participantHelpers';
import StudentInteractionPanel from '../StudentInteractionPanel';
import StudentNamesPanel from '../StudentNamesPanel';
import TeacherPaceMeter from '../TeacherPaceMeter';

// Define participant state types
type ParticipantState = {
  hasActiveDetection: boolean;
  lastAttemptTime: number;
  cleanup: (() => void) | null;
  videoAvailable: boolean;
  lastInitializationId?: number;
};

interface DyteVideoTrack extends MediaStreamTrack {
  enabled: boolean;
  readyState: MediaStreamTrackState;
}

// Store to track participant join order - MODIFIED to include active status
type ParticipantOrderEntry = {
  joinTime: number;
  order: number;
  isActive: boolean;
  userId?: string; // Store the actual user ID for persistence
  name?: string; // Store participant name for reference
};

type ParticipantOrderMap = Map<string, ParticipantOrderEntry>;

// Helper functions for localStorage persistence
const getStorageKey = (classId: string | number) => `participant_order_${classId}`;

const saveParticipantOrder = (classId: string | number, orderMap: ParticipantOrderMap) => {
  try {
    const storageKey = getStorageKey(classId);
    const dataToSave = Array.from(orderMap.entries()).map(([id, entry]) => ({
      id,
      ...entry,
    }));
    localStorage.setItem(storageKey, JSON.stringify(dataToSave));
  } catch (error) {
    datadogLogs.logger.error('Failed to save participant order', error as Error);
  }
};

const loadParticipantOrder = (classId: string | number): ParticipantOrderMap => {
  try {
    const storageKey = getStorageKey(classId);
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      const data = JSON.parse(saved);
      const orderMap = new Map<string, ParticipantOrderEntry>();

      // Check if data is from within the last 24 hours (you can adjust this time window)
      const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;

      data.forEach((item: any) => {
        // Only restore if the join time is within the last 24 hours
        if (item.joinTime > oneDayAgo) {
          orderMap.set(item.id, {
            joinTime: item.joinTime,
            order: item.order,
            isActive: false, // Start with inactive, will be updated when participants join
            userId: item.userId,
            name: item.name,
          });
        }
      });

      return orderMap;
    }
  } catch (error) {
    datadogLogs.logger.error('Failed to load participant order', error as Error);
  }
  return new Map();
};

// Clean up old class data (optional - to prevent localStorage bloat)
const cleanupOldClassData = () => {
  try {
    const keys = Object.keys(localStorage);
    const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;

    keys.forEach((key) => {
      if (key.startsWith('participant_order_')) {
        try {
          const data = JSON.parse(localStorage.getItem(key) || '[]');
          // If all entries are older than 24 hours, remove this key
          const hasRecentData = data.some((item: any) => item.joinTime > oneDayAgo);
          if (!hasRecentData && data.length > 0) {
            localStorage.removeItem(key);
          }
        } catch {
          // If we can't parse it, remove it
          localStorage.removeItem(key);
        }
      }
    });
  } catch (error) {
    datadogLogs.logger.error('Failed to cleanup old class data', error as Error);
  }
};

export default function ActiveSpeaker(
  props: Omit<HTMLAttributes<HTMLRtkParticipantTileElement>, 'style'> & {
    isSmall?: true;
    source?: string | null;
    studentId?: string | null;
    forTutorManager?: boolean;
    isStacked?: boolean;
    socket?: Socket | null;
  },
) {
  // The socket stays here; everything else is still handed down to each tile.
  const { socket, ...tileProps } = props;
  const { isSmall, source, studentId, forTutorManager, isStacked } = tileProps;

  // Hook(s)
  const { meeting, classId, joinedParticipants, socketUser, tutorId } = useMeeting();
  const [size, states] = useMeetingStore((s) => [s.size, s.states]);
  const overlayPanelsEnabled = useOverlayPanels();


  // No longer need to track first 2 minutes since we show all panels

  // Ref(s)
  const initTimersRef = useRef<Map<string, number>>(new Map());
  const isMountedRef = useRef(true);
  const participantStatesRef = useRef<Map<string, ParticipantState>>(new Map());

  // Initialize participantOrderRef with saved data
  const participantOrderRef = useRef<ParticipantOrderMap>(
    classId ? loadParticipantOrder(classId) : new Map(),
  );

  // Calculate the next order number based on existing entries
  const nextOrderRef = useRef(1);

  // Reserve at least one tutor slot at the top of the grid. Grows to the highest
  // tutor count seen this session so a tutor leaving doesn't let students slide up.
  const tutorSlotCountRef = useRef(1);

  // Initialize nextOrderRef based on loaded data
  useEffect(() => {
    if (participantOrderRef.current.size > 0) {
      // Find the highest order number that's not 0 (tutor)
      const maxOrder = Math.max(
        ...Array.from(participantOrderRef.current.values())
          .filter((entry) => entry.order > 0)
          .map((entry) => entry.order),
        0,
      );
      nextOrderRef.current = maxOrder + 1;
    }

    // Clean up old data on mount
    cleanupOldClassData();
  }, []);

  // No longer need the timer for first 2 minutes since we show all panels

  // Constants
  const VIDEO_INIT_DELAY = 3000;

  // Save order to localStorage whenever it changes
  const persistOrder = useCallback(() => {
    if (classId) {
      saveParticipantOrder(classId, participantOrderRef.current);
    }
  }, [classId]);

  // Never a tile: an agent/recorder bot (tagged VIEWER by the LiveKit adapter —
  // see isInClassBotIdentity) competing for a grid slot is the bug this guards.
  // Below, `isTutorParticipant` treats anything else as a student, so this has
  // to run before that split, not be folded into it.
  const realParticipants = useMemo(
    () => (joinedParticipants || []).filter(
      (participant) => participant?.presetName !== PRESET_TYPE.VIEWER,
    ),
    [joinedParticipants],
  );

  // Filter participants for parent view
  const filteredParticipants = useMemo(() => {
    if (!realParticipants.length) {
      return [];
    }

    // If source is parent, filter to show only tutor and specific student
    if (source === 'parent') {
      return realParticipants.filter((participant) =>
        shouldShowForParent(participant, studentId),
      );
    }

    return realParticipants;
  }, [realParticipants, source, studentId]);

  // Students currently away (tab switch / full screen exit), for the tile chip.
  const presentStudentIds = useMemo(
    () =>
      filteredParticipants
        .filter((p) => !isTutorParticipant(p?.presetName))
        .map((p) => extractUserIdFromParticipantId(p?.customParticipantId || p?.id))
        .filter((id): id is string => !!id),
    [filteredParticipants],
  );
  const studentAway = useStudentAway(socket, classId ? String(classId) : null, presentStudentIds);

  // Sort participants based on their order
  const sortedParticipants = useMemo(() => {
    if (!filteredParticipants || filteredParticipants.length === 0) {
      // For parent view, return 2 slots; otherwise empty
      return source === 'parent' ? Array(2).fill(null) : [];
    }

    // Get current participant IDs
    const currentParticipantIds = new Set(
      filteredParticipants.map((p) => p?.customParticipantId || p?.id).filter(Boolean),
    );

    // First, mark all tracked participants as inactive
    participantOrderRef.current.forEach((entry, participantId) => {
      entry.isActive = currentParticipantIds.has(participantId);
    });

    // Then, ensure all current participants have an order
    filteredParticipants.forEach((participant) => {
      const participantId = participant?.customParticipantId || participant?.id;
      if (!participantId) return;

      // Extract user ID from participantId (assuming format is something-userId)
      const userId = extractUserIdFromParticipantId(participantId);

      // Check if we already have this participant tracked
      const existingEntry = participantOrderRef.current.get(participantId);

      if (!existingEntry) {
        // Check if we have this user with a different session ID
        let foundExistingUser = false;
        let existingOrder = -1;
        let existingJoinTime = 0;

        // Look for the same user ID in existing entries
        for (const [storedId, entry] of participantOrderRef.current.entries()) {
          if (entry.userId === userId && storedId !== participantId) {
            // Found the same user with a different session ID
            existingOrder = entry.order;
            existingJoinTime = entry.joinTime;
            foundExistingUser = true;
            // Remove the old entry
            participantOrderRef.current.delete(storedId);
            break;
          }
        }

        // Determine if this is a tutor or student
        if (isTutorParticipant(participant.presetName)) {
          // Tutors always get order 0. Preserve joinTime across refresh so tutor
          // tile ordering (which sorts by joinTime) stays stable.
          participantOrderRef.current.set(participantId, {
            joinTime: foundExistingUser ? existingJoinTime : Date.now(),
            order: 0,
            isActive: true,
            userId: userId,
            name: participant.name,
          });
        } else if (foundExistingUser && existingOrder >= 0) {
          // Returning user gets their previous order and original joinTime
          participantOrderRef.current.set(participantId, {
            joinTime: existingJoinTime,
            order: existingOrder,
            isActive: true,
            userId: userId,
            name: participant.name,
          });
        } else {
          // New student gets the next available order
          participantOrderRef.current.set(participantId, {
            joinTime: Date.now(),
            order: nextOrderRef.current++,
            isActive: true,
            userId: userId,
            name: participant.name,
          });
        }
      } else {
        // Existing participant returning - mark as active and update name
        existingEntry.isActive = true;
        existingEntry.name = participant.name;
        existingEntry.userId = userId;
      }
    });

    // Persist the updated order
    persistOrder();

    // For parent view, use simple 2-slot layout (tutor at 0, student at 1)
    if (source === 'parent') {
      const participantSlots: (any | null)[] = [null, null];
      filteredParticipants.forEach((participant) => {
        if (isTutorParticipant(participant.presetName)) {
          participantSlots[0] = participant;
        } else {
          participantSlots[1] = participant;
        }
      });
      return participantSlots;
    }

    // Render all tutors first (in join-time order), then students in their persisted slots.
    // Tutors are tracked in participantOrderRef with order=0 only for join-time bookkeeping;
    // their on-screen position comes from the sort below, not from `order`.
    const tutors = filteredParticipants
      .filter((p) => isTutorParticipant(p?.presetName))
      .sort((a, b) => {
        const aId = a?.customParticipantId || a?.id;
        const bId = b?.customParticipantId || b?.id;
        const aEntry = aId ? participantOrderRef.current.get(aId) : undefined;
        const bEntry = bId ? participantOrderRef.current.get(bId) : undefined;
        return (aEntry?.joinTime || 0) - (bEntry?.joinTime || 0);
      });

    const students = filteredParticipants.filter((p) => !isTutorParticipant(p?.presetName));

    // Student slots are 1-indexed in participantOrderRef (order >= 1); slot index = order - 1.
    const maxStudentOrder = Math.max(
      ...Array.from(participantOrderRef.current.values())
        .filter((entry) => entry.isActive && entry.order > 0)
        .map((entry) => entry.order),
      0,
    );
    const studentSlotCount = Math.max(students.length, maxStudentOrder);
    const studentSlots: (any | null)[] = new Array(studentSlotCount).fill(null);

    students.forEach((participant) => {
      const participantId = participant?.customParticipantId || participant?.id;
      if (!participantId) return;
      const orderEntry = participantOrderRef.current.get(participantId);
      if (
        orderEntry &&
        orderEntry.isActive &&
        orderEntry.order > 0 &&
        orderEntry.order - 1 < studentSlots.length
      ) {
        studentSlots[orderEntry.order - 1] = participant;
      }
    });

    // Reserve tutor slots so students never slide up when a tutor leaves.
    // The count only grows during the session — once a tutor joins, their slot stays.
    const tutorSlotCount = Math.max(tutorSlotCountRef.current, tutors.length, 1);
    if (tutorSlotCount > tutorSlotCountRef.current) {
      tutorSlotCountRef.current = tutorSlotCount;
    }
    const tutorSlots: (any | null)[] = new Array(tutorSlotCount).fill(null);
    tutors.forEach((tutor, idx) => {
      if (idx < tutorSlots.length) {
        tutorSlots[idx] = tutor;
      }
    });

    return [...tutorSlots, ...studentSlots];
  }, [filteredParticipants, persistOrder, source]);

  // Check if video track is valid and usable
  const isVideoTrackUsable = useCallback((videoTrack: DyteVideoTrack | undefined): boolean => {
    return Boolean(videoTrack && videoTrack.enabled && videoTrack.readyState === 'live');
  }, []);

  // Safely clean up detection for a participant
  const cleanupDetection = useCallback((participantId: string) => {
    const state = participantStatesRef.current.get(participantId);

    if (state) {
      // Call cleanup function if it exists
      if (state.cleanup && typeof state.cleanup === 'function') {
        try {
          state.cleanup();
        } catch (error) {
          datadogLogs.logger.error('Error during cleanup', { participantId }, error as Error);
        }
      }

      // Clear any pending initialization timer
      const existingTimer = initTimersRef.current.get(participantId);
      if (existingTimer) {
        clearTimeout(existingTimer);
        initTimersRef.current.delete(participantId);
      }

      // Update state but keep in map for tracking
      participantStatesRef.current.set(participantId, {
        ...state,
        cleanup: null,
        hasActiveDetection: false,
        lastInitializationId: undefined, // Clear any initialization ID
      });
    }
  }, []);

  // Initialize emotion detection for a participant
  // Entropik emotion tracking disabled
  const initializeEmotionDetection = useCallback(
    async (_participant: any, _participantId: string): Promise<boolean> => {
      return false;
    },
    [],
  );

  // Update handleVideoUpdate function to prevent multiple initialization attempts
  const handleVideoUpdate = useCallback(
    (participant: any) => {
      // Skip if component unmounted
      if (!isMountedRef.current) return;

      const participantId = participant?.customParticipantId;
      if (!participantId) return;

      const videoTrack = participant?.videoTrack as DyteVideoTrack | undefined;

      // Get current state
      const currentState = participantStatesRef.current.get(participantId) || {
        hasActiveDetection: false,
        lastAttemptTime: 0,
        cleanup: null,
        videoAvailable: false,
      };

      // Check if video is now available or was turned off
      const videoUsable = isVideoTrackUsable(videoTrack);
      const wasVideoAvailable = currentState.videoAvailable;

      // Only process if video state actually changed
      if (videoUsable !== wasVideoAvailable || !currentState.hasActiveDetection) {
        // Update video state
        currentState.videoAvailable = videoUsable;
        participantStatesRef.current.set(participantId, currentState);

        if (videoUsable) {
          // Clear any existing initialization timer
          const existingTimer = initTimersRef.current.get(participantId);
          if (existingTimer) {
            clearTimeout(existingTimer);
            initTimersRef.current.delete(participantId);
          }

          // Only schedule initialization if not already detected
          // AND if we don't have a pending timer already
          if (!currentState.hasActiveDetection && !initTimersRef.current.has(participantId)) {
            const timerId = window.setTimeout(() => {
              if (isMountedRef.current) {
                // Double check the participant still exists and still has usable video
                const updatedParticipant = filteredParticipants.find(
                  (p) => p.customParticipantId === participantId,
                );

                if (updatedParticipant) {
                  const currentVideoTrack = updatedParticipant?.videoTrack as
                    | DyteVideoTrack
                    | undefined;
                  const stillUsable = isVideoTrackUsable(currentVideoTrack);

                  if (stillUsable) {
                    initializeEmotionDetection(updatedParticipant, participantId);
                  }
                }
              }
              initTimersRef.current.delete(participantId); // Clean up timer reference
            }, VIDEO_INIT_DELAY);

            initTimersRef.current.set(participantId, timerId);
          }
        } else if (currentState.hasActiveDetection) {
          // If video was turned off and had detection, clean up
          cleanupDetection(participantId);
        }
      }
    },
    [cleanupDetection, initializeEmotionDetection, isVideoTrackUsable, filteredParticipants],
  );

  // Single combined useEffect for participant management
  useEffect(() => {
    // Set mounted flag
    isMountedRef.current = true;

    if (socketUser === PRESET_TYPE.VIEWER || NODE_ENV !== 'prod') return;

    if (!filteredParticipants?.length) {
      return;
    }

    // Get current participant IDs
    const currentParticipantIds = new Set(
      filteredParticipants.map((p) => p.customParticipantId).filter((id): id is string => !!id),
    );

    // Clean up states for participants who left (but DON'T delete from participantOrderRef)
    for (const participantId of participantStatesRef.current.keys()) {
      if (!currentParticipantIds.has(participantId)) {
        cleanupDetection(participantId);
        participantStatesRef.current.delete(participantId);
        // Mark as inactive in order ref but keep the entry
        const orderEntry = participantOrderRef.current.get(participantId);
        if (orderEntry) {
          orderEntry.isActive = false;
          persistOrder(); // Save the updated state
        }
      }
    }

    // Set up event listeners and initialize for current participants
    filteredParticipants?.forEach((participant) => {
      const id = participant?.customParticipantId;
      if (!id) return;

      // Set up video update event listener if not already set (Dyte-specific)
      // Type cast to any since event methods are provider-specific
      const p = participant as any;
      if (typeof p.off === 'function' && typeof p.on === 'function') {
        p.off('videoUpdate', handleVideoUpdate); // Remove any existing to prevent duplicates
        p.on('videoUpdate', handleVideoUpdate);
      }

      // Initial check of video state
      handleVideoUpdate(participant);

      // Try initial detection if needed
      const state = participantStatesRef.current.get(id);
      if (state?.videoAvailable && !state.hasActiveDetection) {
        initializeEmotionDetection(participant, id);
      }
    });

    // Cleanup all on unmount
    return () => {
      // Set unmounted flag first
      isMountedRef.current = false;

      // Clear all timers
      initTimersRef.current.forEach((timerId) => {
        clearTimeout(timerId);
      });
      initTimersRef.current.clear();

      // Clean up event listeners
      if (filteredParticipants) {
        filteredParticipants.forEach((participant) => {
          const p = participant as any;
          if (p && typeof p.off === 'function') {
            try {
              p.off('videoUpdate', handleVideoUpdate);
            } catch (error) {
              datadogLogs.logger.error(
                'Error removing event listener',
                { userId: participant?.customParticipantId },
                error as Error,
              );
            }
          }
        });
      }

      // Clean up all detections
      Array.from(participantStatesRef.current.keys()).forEach((participantId) => {
        try {
          cleanupDetection(participantId);
        } catch (error) {
          datadogLogs.logger.error(
            'Error cleaning up detection',
            { participantId },
            error as Error,
          );
        }
      });

      participantStatesRef.current.clear();
    };
  }, [
    filteredParticipants,
    socketUser,
    cleanupDetection,
    handleVideoUpdate,
    initializeEmotionDetection,
    persistOrder,
  ]);

  // Student talk time tracking
  if (socketUser !== PRESET_TYPE.VIEWER) {
    useStudentTalkTime();
  }

  // Message handler
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const { data } = event;
      if (data?.type === 'SEND_MESSAGE') {
        try {
          (meeting as any).participants.broadcastMessage('tutorNudge', {
            data: event?.data?.payload?.data,
          });
        } catch (error) {
          datadogLogs.logger.error(
            `Failed to send message`,
            { error: error instanceof Error ? error.message : 'Unknown error' },
            error as Error,
          );
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [tutorId, meeting]);

  // Hide panels unless explicitly opted in via ?panels=true, and always for
  // parent source or tutor manager. Each panel drives its own 10s poll against
  // lms-backend, so keeping them unmounted is what actually stops the requests.
  const hidePanels = !overlayPanelsEnabled || source === 'parent' || forTutorManager;

  return (
    <div className='w-full h-full bg-black rounded-xl flex flex-col'>
      {/* Show all panels at the same time - hide for parent source and tutor manager */}
      {classId && !hidePanels && (
        <div className='px-2 pt-2 shrink-0' style={{ zoom: 0.5 }}>
          <StudentNamesPanel classId={classId} />
          <div style={{ display: 'none' }}>
            <StudentInteractionPanel classId={classId} />
          </div>
          <TeacherPaceMeter classId={classId} />
        </div>
      )}
      <div
        className={`w-full p-2 flex-1 min-h-0 ${
          isStacked ? 'overflow-hidden' : 'overflow-y-auto'
        }`}
      >
        <div
          className={
            isStacked
              ? 'flex h-full w-full gap-2'
              : `grid ${source === 'parent' ? 'grid-cols-1 grid-rows-2 h-full' : 'grid-cols-2'} w-full gap-2`
          }
          style={!isStacked && source !== 'parent' ? { gridAutoRows: 'auto' } : undefined}
        >
          {sortedParticipants.map((participant, index) => {
            return (
              <div
                key={participant?.id || `empty-slot-${index}`}
                className={
                  isStacked
                    ? // Share the row equally so every tile fits - the strip never scrolls
                      'h-full flex-1 min-w-0'
                    : `w-full ${source === 'parent' ? 'h-full' : 'aspect-square'}`
                }
              >
                {participant ? (
                  <ParticipantTile
                    key={participant.id}
                    participant={participant}
                    meeting={meeting}
                    size={isSmall ? 'lg' : size}
                    states={states}
                    {...tileProps}
                    isAway={
                      !isTutorParticipant(participant.presetName) &&
                      !!studentAway[
                        extractUserIdFromParticipantId(
                          participant.customParticipantId || participant.id,
                        ) ?? ''
                      ]
                    }
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      backgroundColor: '#000000',
                    }}
                    className='!w-full !h-full rounded-lg shadow-lg overflow-hidden'
                  />
                ) : (
                  <div className='flex items-center justify-center w-full h-full bg-black border border-gray-800 rounded-lg'>
                    <div className='text-sm text-gray-600'>
                      <svg
                        className='w-8 h-8 mx-auto mb-2'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={1.5}
                          d='M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'
                        />
                      </svg>
                      <span>Empty Slot</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
